import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BiLogOut } from "react-icons/bi";
import {
  FaAd,
  FaBars,
  FaComment,
  FaEnvelope,
  FaKey,
  FaPlus,
  FaUser,
  FaUserGraduate,
  FaUsers,
} from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import logo from "../../assets/logo.png";
import { logout } from "../../redux/userSlice";
import LanguageSelector from "../LanguageSelector";
import Avatar from "../user/Avatar";
import "./Navbar.css"; // Import the CSS file

const Navbar = () => {
  const { t } = useTranslation();
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  const auth = useSelector((state) => state.auth); // Retrieve auth data
  const user = auth?.user;
  const toggleDropdown = () => {
    setDropdownOpen((prev) => !prev);
  };

  const handleLogout = () => {
    dispatch(logout());
    setDropdownOpen(false);
    navigate("/home");
  };

  // Close the dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  const [unreadMessages, setUnreadMessages] = useState(0);
  useEffect(() => {
    // Fetch unread message count when user logs in
    if (user) {
      axios
        .get(`${process.env.REACT_APP_BACKEND_URL}/message_box.php`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${auth.token}` }, // Include token in headers
        })
        .then((res) => {
          if (res.data.success && typeof res.data.unreadCount === "number") {
            setUnreadMessages(res.data.unreadCount);
          }
        })
        .catch(() => setUnreadMessages(0));
    } else {
      setUnreadMessages(0);
    }
  }, [user, auth.token]);

  // Responsive menu state
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Close mobile menu on route change or dropdown close
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [isDropdownOpen, navigate]);

  // Handle window resize to close mobile menu if screen is resized to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) setMobileMenuOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <nav className="navbar p-1 shadow-md">
      <div className="navbar-container text-base gap-4">
        {/* Logo Row */}
        <div className="flex gap-4 items-center">
          <div className="logo">
            <Link to="../">
              <img src={logo} alt="logo" width={60} height={60} />
            </Link>
          </div>
          <Link to="../SearchTutor" className="hidden md:flex text-orange-500">
            <FaUserGraduate size={23} className="mr-2" />
            {t("SearchTutor")}
          </Link>
          <Link to="../SearchStudent" className="hidden md:flex text-green-500">
            <FaUsers size={24} className="mr-2" />
            {t("SearchStudent")}
          </Link>
          <Link to="../tutorCenters" className="hidden md:flex text-blue-500">
            <FaComment size={23} className="mr-2" />
            Tutor Centers
          </Link>
          <Link to="../math-zoo" className="hidden md:flex text-purple-500">
            🦁 數學動物園
          </Link>
          {/* Hamburger Icon for mobile */}
          <button
            className="md:hidden flex items-center px-3 py-2    "
            onClick={() => setMobileMenuOpen((prev) => !prev)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <FaBars size={24} /> : <FaBars size={24} />}
          </button>
        </div>

        <div
          className={`nav-links md:flex md:flex-row items-center ${
            isMobileMenuOpen
              ? "flex flex-col items-start absolute top-16 left-0 w-full bg-white z-20 shadow-md p-4"
              : "hidden md:flex"
          }`}
        >
          <div className="flex flex-col md:flex-row items-center w-full md:w-auto">
            <Link
              to="../SearchTutor"
              className="md:hidden flex text-orange-500 mb-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <FaUserGraduate size={23} className="mr-2" />
              {t("SearchTutor")}
            </Link>
            <Link
              to="../SearchStudent"
              className="md:hidden flex text-green-500 mb-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <FaUsers size={24} className="mr-2" />
              {t("SearchStudent")}
            </Link>
            <Link
              to="../tutorCenters"
              className="md:hidden flex text-blue-500 mb-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <FaComment size={23} className="mr-2" />
              Tutor Centers
            </Link>
            <Link
              to="../math-zoo"
              className="md:hidden flex text-purple-500 mb-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              🦁 數學動物園
            </Link>

            <Link
              to="/ads"
              onClick={() => {
                setDropdownOpen(false);
                setMobileMenuOpen(false);
              }}
              className="flex items-center px-4 py-2 hover:text-primary font-semibold"
            >
              <FaPlus size={27} className="mr-2" />
              <FaAd size={28} className="mr-2" />
              {t("createAd")}
            </Link>
            <Link
              to="/privacy-policy"
              className="mx-2 hover:text-primary font-semibold"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t("privacyPolicy")}
            </Link>
            <Link
              to="/terms"
              className="mx-2 hover:text-primary font-semibold"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t("termsOfService")}
            </Link>
          </div>
        </div>
        {/* Message Icon with badge */}
        {user && (
          <Link to="/messages" className="relative flex items-center mr-2">
            <FaEnvelope size={26} className="text-blue-500" />
            {unreadMessages > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full px-2 text-xs font-bold">
                {unreadMessages}
              </span>
            )}
          </Link>
        )}
        <div className=" flex flex-row " ref={dropdownRef}>
          {user ? (
            <div className="relative dropdown" ref={dropdownRef}>
              <button
                onClick={toggleDropdown}
                className="dropdown-toggle flex items-center"
                aria-haspopup="true"
                aria-expanded={isDropdownOpen}
              >
                <Avatar
                  width={40}
                  height={40}
                  name={user?.username}
                  imageUrl={user?.profilePicture}
                  userId={user?.id}
                />
              </button>
              {isDropdownOpen && (
                <ul className="dropdown-menu absolute right-0 mt-2 bg-white shadow-lg rounded z-10">
                  <li>
                    <Link
                      to="/myAds"
                      onClick={() => {
                        setDropdownOpen(false);
                        setMobileMenuOpen(false);
                      }}
                      className="flex items-center px-4 py-2 hover:bg-gray-100"
                    >
                      <FaAd size={20} className="mr-2" />
                      {t("myAds")}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/profile"
                      onClick={() => {
                        setDropdownOpen(false);
                        setMobileMenuOpen(false);
                      }}
                      className="flex items-center px-4 py-2 hover:bg-gray-100"
                    >
                      <FaUser size={20} className="mr-2" />
                      {t("profile")}
                    </Link>
                    <Link
                      to="/changepassword"
                      onClick={() => {
                        setDropdownOpen(false);
                        setMobileMenuOpen(false);
                      }}
                      className="flex items-center px-4 py-2 hover:bg-gray-100"
                    >
                      <FaKey size={20} className="mr-2" />
                      {t("changePassword")}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/comments"
                      onClick={() => {
                        setDropdownOpen(false);
                        setMobileMenuOpen(false);
                      }}
                      className="flex items-center px-4 py-2 hover:bg-gray-100"
                    >
                      <FaComment size={20} className="mr-2" />
                      {t("comment")}
                    </Link>
                  </li>
                  <li>
                    <Link
                      onClick={() => {
                        handleLogout();
                        setMobileMenuOpen(false);
                      }}
                      className="flex items-center px-4 py-2 hover:bg-gray-100"
                      title={t("logout")}
                    >
                      <BiLogOut size={20} className="mr-2" />
                      {t("logout")}
                    </Link>
                  </li>
                </ul>
              )}
            </div>
          ) : (
            <Link
              to="/login"
              className="flex items-center hover:text-primary font-semibold mb-2 md:mb-0"
              onClick={() => setMobileMenuOpen(false)}
            >
              <FaUser size={20} className="mr-2" />
              {t("login")}
            </Link>
          )}
          <LanguageSelector />
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
